import { useMemo } from "react";
import { Accordion, Stack, Table, Text } from "@mantine/core";
import type {
  LegacyCaseDetail,
  LegacyExtractionSummary,
} from "../../../types/legacyCases";
import LegacySourceComparison from "./LegacySourceComparison";

interface Props {
  detail: LegacyCaseDetail;
  sourceRowId: string;
  extraction?: LegacyExtractionSummary;
}

export default function LegacySourceEvidence({
  detail,
  sourceRowId,
  extraction,
}: Props) {
  const row = useMemo(
    () => detail.sourceRows.find((entry) => entry.id === sourceRowId),
    [detail.sourceRows, sourceRowId]
  );
  return (
    <Accordion multiple variant="contained">
      {row && (
        <Accordion.Item value="comparison">
          <Accordion.Control>
            Compare patient, case, and report values
          </Accordion.Control>
          <Accordion.Panel>
            <LegacySourceComparison
              spreadsheet={row.normalized}
              extraction={extraction}
            />
          </Accordion.Panel>
        </Accordion.Item>
      )}
      <Accordion.Item value="spreadsheet">
        <Accordion.Control>Original spreadsheet values</Accordion.Control>
        <Accordion.Panel>
          {row ? (
            <Stack gap="sm">
              <Text size="sm">
                {row.sourceFileName} · {row.sheetName} · row {row.rowNumber}
              </Text>
              <Table.ScrollContainer minWidth={300}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Field</Table.Th>
                      <Table.Th>Original value</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {row.rawCells.map((cell) => (
                      <Table.Tr key={cell.columnIndex}>
                        <Table.Td>
                          {cell.header || `Column ${cell.columnIndex + 1}`}
                        </Table.Td>
                        <Table.Td>
                          <Text
                            size="sm"
                            style={{
                              whiteSpace: "pre-wrap",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {cell.formattedValue ??
                              (typeof cell.value === "string"
                                ? cell.value
                                : JSON.stringify(cell.value)) ??
                              "—"}
                          </Text>
                          {cell.formula && (
                            <Text size="xs" c="dimmed">
                              Formula: {cell.formula}
                            </Text>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Stack>
          ) : (
            <Text>Select a source row.</Text>
          )}
        </Accordion.Panel>
      </Accordion.Item>
      <Accordion.Item value="report">
        <Accordion.Control>
          Original report fields and complete text
        </Accordion.Control>
        <Accordion.Panel>
          {extraction ? (
            <Stack gap="md">
              <Text size="sm">
                Extraction {new Date(extraction.createdAt).toLocaleString()} ·{" "}
                {extraction.result.parserVersion}
              </Text>
              {Object.entries(extraction.result.fields).map(
                ([field, values]) => (
                  <Stack gap={4} key={field}>
                    <Text size="sm" fw={600}>
                      {field
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (letter) => letter.toUpperCase())}
                    </Text>
                    {values.map((value, index) => (
                      <Text
                        size="sm"
                        key={index}
                        style={{ whiteSpace: "pre-wrap" }}
                      >
                        {value.rawValue} (page{" "}
                        {value.spans.map((span) => span.pageNumber).join(", ")})
                      </Text>
                    ))}
                  </Stack>
                )
              )}
              <Text
                component="pre"
                size="sm"
                style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {extraction.result.sourceText}
              </Text>
            </Stack>
          ) : (
            <Text>
              No extraction selected. Extract the final report to inspect its
              source text.
            </Text>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
